import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const CONSENT_KEY = 'fish_cookie_consent';
const CONSENT_VERSION = '1';

function getStoredConsent(): (ConsentState & { version: string }) | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(consent: ConsentState) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, version: CONSENT_VERSION }));
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Slight delay so page renders first
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = (all: boolean) => {
    const consent: ConsentState = {
      necessary: true,
      analytics: all ? true : analytics,
      marketing: all ? true : marketing,
    };
    storeConsent(consent);
    setVisible(false);
  };

  const savePreferences = () => {
    accept(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-background border-t border-border shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl" aria-hidden>🍪</span>
            <h2 className="font-bold text-foreground text-base md:text-lg">We use cookies</h2>
          </div>
          <button
            onClick={() => accept(false)}
            aria-label="Close cookie banner without changing preferences"
            className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          We use cookies to ensure this website works correctly and to understand how it's used. 
          You can choose which optional cookies to allow. Essential cookies are always active as they are 
          necessary for the website to function. For more information, see our{' '}
          <a href="/policies#cookies" className="underline text-primary hover:text-primary/80">cookies policy</a>.
        </p>

        {/* Expandable details */}
        <div className="mb-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-sm text-primary hover:underline font-medium"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? 'Hide cookie details' : 'Customise preferences'}
          </button>

          {showDetails && (
            <div className="mt-3 space-y-3 border border-border rounded-lg p-4 bg-muted/40">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-sm text-foreground">Essential cookies</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Required for the website to function (login sessions, security). Cannot be disabled.
                  </p>
                </div>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded shrink-0">Always on</span>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-sm text-foreground">Analytics cookies</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Help us understand how visitors use the site so we can improve it. No personal data is sold.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    aria-label="Allow analytics cookies"
                  />
                  <div className="w-10 h-6 bg-muted rounded-full peer peer-checked:bg-primary transition-colors" />
                  <div className="absolute left-1 top-1 w-4 h-4 bg-background rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </label>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-sm text-foreground">Marketing cookies</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Used to show relevant content and measure the effectiveness of our communications.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    aria-label="Allow marketing cookies"
                  />
                  <div className="w-10 h-6 bg-muted rounded-full peer peer-checked:bg-primary transition-colors" />
                  <div className="absolute left-1 top-1 w-4 h-4 bg-background rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {showDetails ? (
            <>
              <Button
                onClick={savePreferences}
                variant="outline"
                className="flex-1 sm:flex-none border-border"
              >
                Save my preferences
              </Button>
              <Button
                onClick={() => accept(true)}
                className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Accept all cookies
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => { setShowDetails(true); }}
                variant="outline"
                className="flex-1 sm:flex-none border-border"
              >
                Manage preferences
              </Button>
              <Button
                onClick={() => accept(false)}
                variant="outline"
                className="flex-1 sm:flex-none border-border"
              >
                Accept essential only
              </Button>
              <Button
                onClick={() => accept(true)}
                className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Accept all cookies
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook to read consent state anywhere in the app
export function useCookieConsent(): ConsentState {
  const stored = getStoredConsent();
  return {
    necessary: true,
    analytics: stored?.analytics ?? false,
    marketing: stored?.marketing ?? false,
  };
}
